import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import ReactPlayer from 'react-player';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import classNames from 'classnames';

import {
  bottomBannerSelector,
  close,
  initialize,
} from 'store/slices/bottomBanner';
import { sidebarSelector } from 'store/slices/sidebar';

import { findPathPages } from 'utils/pages';

import Button from 'components/UI/Button/Button';

import { PAGE_PATH } from 'components/config/pages';
import { isSafari } from 'config/client';

import Banner from 'images/BottomBanners/banner.webm';
import BannerWebp from 'images/BottomBanners/banner.webp';
import BannerPreload from 'images/BottomBanners/bannerPreload.webp';
import { ReactComponent as CloseIcon } from 'images/Icons/Close.svg';

import styles from './BottomBanner.module.scss';

const primaryButtonStyles = {
  height: '48px',
  padding: '10px 20px',
  fontWeight: '700',
  fontSize: '24px',
  textTransform: 'uppercase',
  borderRadius: '100px',
  pointerEvents: 'none',
};

const secondaryButtonStyles = {
  borderColor: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#FFFFFF',
  },
};

const TOURNAMENT_ID = 'tn_1000733';

const BottomBanner = () => {
  const dispatch = useDispatch();

  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  const isVisible = useSelector(bottomBannerSelector.getVisible);
  const isVisibleSidebar = useSelector(sidebarSelector.getVisible);

  const isEnabled = useFeatureIsOn('ca-bottom-banner');

  const handleClose = useCallback(() => dispatch(close()), []);
  const handleLoad = useCallback(() => setIsMediaLoaded(true), []);

  const show = useMemo(() => isEnabled && isVisible, [isEnabled, isVisible]);

  useEffect(() => {
    dispatch(initialize());
  }, []);

  if (!show) return null;

  return (
    <div
      className={classNames(styles.root, {
        [styles.sidebarHidden]: !isVisibleSidebar,
      })}
    >
      <div className={styles.mediaWrapper}>
        {!isMediaLoaded && (
          <img
            src={BannerPreload}
            alt="banner preload"
            data-testid="bottom-banner-preload"
          />
        )}
        {!isSafari ? (
          <ReactPlayer
            url={Banner}
            width="100%"
            height="100%"
            onReady={handleLoad}
            data-testid="bottom-banner-video"
            style={{ ...(!isMediaLoaded && { display: 'none' }) }}
            playing
            muted
            loop
          />
        ) : (
          <img
            src={BannerWebp}
            alt="banner img"
            onLoad={handleLoad}
            className={classNames(styles.image, {
              [styles.loaded]: isMediaLoaded,
            })}
            data-testid="bottom-banner-img"
          />
        )}
      </div>
      <CloseIcon
        onClick={handleClose}
        className={styles.closeIcon}
        data-testid="bottom-banner-close"
      />
      <>
        <Button sx={primaryButtonStyles}>
          Celebrate our 8th b-day and win a prize!
        </Button>
        <Button
          variant="secondary"
          component={Link}
          to={findPathPages(PAGE_PATH.TOURNAMENTS_ITEM).replace(
            ':eventId',
            TOURNAMENT_ID,
          )}
          sx={secondaryButtonStyles}
        >
          Join the tournament and have fun
        </Button>
      </>
    </div>
  );
};

export default memo(BottomBanner);
